import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { Calendar, PlugsConnected, Sparkle } from '@phosphor-icons/react';
import { useBusiness } from '../../../context/BusinessContext';
import { useBusinessSubscription } from '../../../hooks/useBusinessSubscription';
import useThemeStyles from '../../../theme/useThemeStyles';
import api from '../../../utils/api';
import UpgradeCTA from '../../Business/UpgradeCTA';
import MailConnectionSettings from '../MailConnection/MailConnectionSettings';
import CalendarSyncSettings from '../../UnifiedBooking/CalendarSyncSettings';
import IntegrationPrivacyConsentDialog from '../Integrations/IntegrationPrivacyConsentDialog';

const OWNER_LIKE = new Set(['owner', 'admin']);

const GMAIL_SVG = `${process.env.PUBLIC_URL}/img/integrations/gmail.svg`;
const OUTLOOK_SVG = `${process.env.PUBLIC_URL}/img/integrations/microsoft-outlook.svg`;
const MAILCHIMP_SVG = `${process.env.PUBLIC_URL}/img/integrations/mailchimp-logo.svg`;

/** Third-party SVGs and trademark notes: public/img/integrations/ATTRIBUTION.txt */
function BrandIcon({ src, alt, size = 28 }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ width: size, height: size, objectFit: 'contain', display: 'block', flexShrink: 0 }}
    />
  );
}

function IntegrationCard({ icon, title, description, children, footer }) {
  const { card } = useThemeStyles();
  return (
    <Card sx={{ ...card.default, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.25 }} aria-hidden>
            {icon}
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        {children}
      </CardContent>
      {footer ? <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>{footer}</CardActions> : null}
    </Card>
  );
}

function SupplierAppsPage() {
  const styles = useThemeStyles();
  const { selectedBusiness } = useBusiness();
  const { hasFeature, loading: subLoading } = useBusinessSubscription();
  const businessId = selectedBusiness?.business?._id;
  const role = String(selectedBusiness?.role || '').toLowerCase();
  const canManageMail = OWNER_LIKE.has(role);

  const [mailLoading, setMailLoading] = useState(false);
  const [mailConnection, setMailConnection] = useState(null);
  const [mailError, setMailError] = useState('');
  const [connectingMail, setConnectingMail] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [mailchimpLoading, setMailchimpLoading] = useState(false);
  const [mailchimpConnection, setMailchimpConnection] = useState(null);
  const [mailchimpError, setMailchimpError] = useState('');
  const [mailchimpSuccess, setMailchimpSuccess] = useState('');
  const [mailchimpAudiences, setMailchimpAudiences] = useState([]);
  const [mailchimpAudiencesLoading, setMailchimpAudiencesLoading] = useState(false);
  const [mailchimpAudienceOpen, setMailchimpAudienceOpen] = useState(false);
  const [selectedAudienceId, setSelectedAudienceId] = useState('');
  const [connectingMailchimp, setConnectingMailchimp] = useState(false);
  const [disconnectingMailchimp, setDisconnectingMailchimp] = useState(false);
  const [mailchimpDisconnectConfirmOpen, setMailchimpDisconnectConfirmOpen] = useState(false);
  const [savingMailchimpAudience, setSavingMailchimpAudience] = useState(false);
  const [privacyConsentIntegration, setPrivacyConsentIntegration] = useState(null);

  const canUseMail = hasFeature('messaging');
  const canUseCalendar = hasFeature('calendar_sync');
  const canUseMailchimp = hasFeature('mailchimp');

  const loadMailStatus = useCallback(async () => {
    if (!businessId || !canUseMail) return;
    setMailLoading(true);
    setMailError('');
    try {
      const response = await api.get('/api/mail-sync/status', { params: { businessId } });
      setMailConnection(response.data.connection || null);
    } catch (err) {
      setMailError(err?.response?.data?.message || 'Failed to load inbox connection');
    } finally {
      setMailLoading(false);
    }
  }, [businessId, canUseMail]);

  const loadMailchimpStatus = useCallback(async () => {
    if (!businessId || !canUseMailchimp) return;
    setMailchimpLoading(true);
    setMailchimpError('');
    try {
      const response = await api.get('/api/mailchimp/status', { params: { businessId } });
      const connection = response.data?.connection || null;
      setMailchimpConnection(connection);
      setSelectedAudienceId(connection?.selectedAudienceId || '');
    } catch (err) {
      setMailchimpError(err?.response?.data?.message || 'Failed to load Mailchimp connection');
    } finally {
      setMailchimpLoading(false);
    }
  }, [businessId, canUseMailchimp]);

  const loadMailchimpAudiences = useCallback(async () => {
    if (!businessId || !canUseMailchimp) return;
    setMailchimpAudiencesLoading(true);
    setMailchimpError('');
    try {
      const response = await api.get('/api/mailchimp/audiences', { params: { businessId } });
      const audiences = Array.isArray(response.data?.audiences) ? response.data.audiences : [];
      setMailchimpAudiences(audiences);
      if (!selectedAudienceId && audiences.length > 0) {
        setSelectedAudienceId(audiences[0].id);
      }
    } catch (err) {
      setMailchimpError(err?.response?.data?.message || 'Failed to load Mailchimp audiences');
    } finally {
      setMailchimpAudiencesLoading(false);
    }
  }, [businessId, canUseMailchimp, selectedAudienceId]);

  useEffect(() => {
    loadMailStatus();
  }, [loadMailStatus]);

  useEffect(() => {
    loadMailchimpStatus();
  }, [loadMailchimpStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mailSync = params.get('mailSync');
    const mailchimp = params.get('mailchimp');
    if (mailSync === 'connected' || mailSync === 'error') {
      loadMailStatus();
    }
    if (mailchimp === 'connected') {
      setMailchimpSuccess('Mailchimp connected successfully');
      loadMailchimpStatus();
    }
    if (mailchimp === 'error') {
      setMailchimpError('Mailchimp authorisation failed. Please try again.');
      loadMailchimpStatus();
    }
  }, [loadMailStatus, loadMailchimpStatus]);

  const handleMailConnect = async (provider) => {
    if (!businessId || !canManageMail) return;
    setMailError('');
    setConnectingMail(provider);
    try {
      const response = await api.get(`/api/mail-sync/${provider}/auth`, { params: { businessId } });
      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl;
        return;
      }
      setMailError('No authorisation URL returned');
      setPrivacyConsentIntegration(null);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Failed to start authorisation';
      if (status === 409) {
        await loadMailStatus();
      }
      setMailError(message);
      setPrivacyConsentIntegration(null);
    } finally {
      setConnectingMail(null);
    }
  };

  const handleMailDisconnect = async () => {
    if (!mailConnection?._id) return;
    setDisconnecting(true);
    setMailError('');
    try {
      await api.delete(`/api/mail-sync/${mailConnection._id}`);
      setMailConnection(null);
      setDisconnectConfirmOpen(false);
    } catch (err) {
      setMailError(err?.response?.data?.message || 'Failed to disconnect inbox');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleMailchimpConnect = async () => {
    if (!businessId || !canManageMail) return;
    setMailchimpError('');
    setMailchimpSuccess('');
    setConnectingMailchimp(true);
    try {
      const response = await api.get('/api/mailchimp/auth', { params: { businessId } });
      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl;
        return;
      }
      setMailchimpError('No authorisation URL returned');
      setPrivacyConsentIntegration(null);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to start Mailchimp authorisation';
      setMailchimpError(message);
      setPrivacyConsentIntegration(null);
    } finally {
      setConnectingMailchimp(false);
    }
  };

  const handlePrivacyConsentConfirm = () => {
    if (privacyConsentIntegration === 'mailchimp') {
      handleMailchimpConnect();
      return;
    }
    if (privacyConsentIntegration === 'google' || privacyConsentIntegration === 'microsoft') {
      handleMailConnect(privacyConsentIntegration);
    }
  };

  const handleOpenMailchimpAudienceDialog = async () => {
    setMailchimpAudienceOpen(true);
    await loadMailchimpAudiences();
  };

  const handleSaveMailchimpAudience = async () => {
    if (!selectedAudienceId || !businessId) {
      setMailchimpError('Please choose an audience first');
      return;
    }
    setSavingMailchimpAudience(true);
    setMailchimpError('');
    try {
      const selectedAudience = mailchimpAudiences.find((audience) => audience.id === selectedAudienceId);
      const response = await api.put('/api/mailchimp/connection/audience', {
        businessId,
        audienceId: selectedAudienceId,
        audienceName: selectedAudience?.name || null
      });
      setMailchimpConnection(response.data?.connection || null);
      setMailchimpAudienceOpen(false);
      setMailchimpSuccess('Mailchimp audience saved');
    } catch (err) {
      setMailchimpError(err?.response?.data?.message || 'Failed to save Mailchimp audience');
    } finally {
      setSavingMailchimpAudience(false);
    }
  };

  const handleMailchimpDisconnect = async () => {
    if (!mailchimpConnection?._id) return;
    setDisconnectingMailchimp(true);
    setMailchimpError('');
    setMailchimpSuccess('');
    try {
      await api.delete(`/api/mailchimp/${mailchimpConnection._id}`);
      setMailchimpConnection(null);
      setSelectedAudienceId('');
      setMailchimpDisconnectConfirmOpen(false);
      setMailchimpSuccess('Mailchimp disconnected');
    } catch (err) {
      setMailchimpError(err?.response?.data?.message || 'Failed to disconnect Mailchimp');
    } finally {
      setDisconnectingMailchimp(false);
    }
  };

  const mailOtherProviderActive = useMemo(() => {
    if (!mailConnection || mailConnection.status !== 'active') return null;
    return mailConnection.provider;
  }, [mailConnection]);

  const mailchimpConnected = useMemo(
    () => Boolean(mailchimpConnection && mailchimpConnection.status === 'active'),
    [mailchimpConnection]
  );

  if (subLoading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Apps
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Connect tools your business already uses. More integrations will land here over time.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            {canUseMail ? (
              <IntegrationCard
                icon={<BrandIcon src={GMAIL_SVG} alt="" size={28} />}
                title="Gmail & Google Workspace"
                description="Sign in with Google so your shared inbox appears in Messages. One mailbox per business."
                footer={
                  mailLoading ? (
                    <CircularProgress size={22} />
                  ) : mailOtherProviderActive === 'google' ? (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" color="success" label="Connected" />
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {mailConnection?.emailAddress}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button size="small" variant="outlined" onClick={() => setMailDialogOpen(true)}>
                          Inbox settings
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setDisconnectConfirmOpen(true)}
                          disabled={!canManageMail}
                        >
                          Disconnect
                        </Button>
                      </Stack>
                    </Stack>
                  ) : mailOtherProviderActive === 'microsoft' ? (
                    <Typography variant="body2" color="text.secondary">
                      Your Outlook inbox is connected. Disconnect it first if you want to use Gmail instead.
                    </Typography>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={!canManageMail || connectingMail !== null}
                      onClick={() => setPrivacyConsentIntegration('google')}
                    >
                      {connectingMail === 'google' ? 'Redirecting…' : 'Connect Google'}
                    </Button>
                  )
                }
              >
                {mailError ? (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {mailError}
                  </Typography>
                ) : null}
                {!canManageMail && !mailOtherProviderActive ? (
                  <Typography variant="caption" color="text.secondary">
                    Only owners or admins can connect an inbox.
                  </Typography>
                ) : null}
              </IntegrationCard>
            ) : (
              <IntegrationCard
                icon={<BrandIcon src={GMAIL_SVG} alt="" size={28} />}
                title="Gmail & Google Workspace"
                description="Sign in with Google so your shared inbox appears in Messages. Included on paid plans with messaging."
              >
                <UpgradeCTA reason="shared inbox and messaging" compact />
              </IntegrationCard>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {canUseMail ? (
              <IntegrationCard
                icon={<BrandIcon src={OUTLOOK_SVG} alt="" size={28} />}
                title="Microsoft 365 / Outlook"
                description="Use the inbox your couples already email. Replies send from this address inside Idotoo."
                footer={
                  mailLoading ? (
                    <CircularProgress size={22} />
                  ) : mailOtherProviderActive === 'microsoft' ? (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" color="success" label="Connected" />
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {mailConnection?.emailAddress}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button size="small" variant="outlined" onClick={() => setMailDialogOpen(true)}>
                          Inbox settings
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setDisconnectConfirmOpen(true)}
                          disabled={!canManageMail}
                        >
                          Disconnect
                        </Button>
                      </Stack>
                    </Stack>
                  ) : mailOtherProviderActive === 'google' ? (
                    <Typography variant="body2" color="text.secondary">
                      Your Google inbox is connected. Disconnect it first if you want to use Outlook instead.
                    </Typography>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={!canManageMail || connectingMail !== null}
                      onClick={() => setPrivacyConsentIntegration('microsoft')}
                    >
                      {connectingMail === 'microsoft' ? 'Redirecting…' : 'Connect Microsoft'}
                    </Button>
                  )
                }
              >
                {!canManageMail && !mailOtherProviderActive ? (
                  <Typography variant="caption" color="text.secondary">
                    Only owners or admins can connect an inbox.
                  </Typography>
                ) : null}
              </IntegrationCard>
            ) : (
              <IntegrationCard
                icon={<BrandIcon src={OUTLOOK_SVG} alt="" size={28} />}
                title="Microsoft 365 / Outlook"
                description="Use the inbox your couples already email. Included on paid plans with messaging."
              >
                <UpgradeCTA reason="shared inbox and messaging" compact />
              </IntegrationCard>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {canUseMailchimp ? (
              <IntegrationCard
                icon={<BrandIcon src={MAILCHIMP_SVG} alt="" size={28} />}
                title="Mailchimp"
                description="Sync couples and opted-in enquiries to your Mailchimp audience."
                footer={
                  mailchimpLoading ? (
                    <CircularProgress size={22} />
                  ) : mailchimpConnected ? (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" color="success" label="Connected" />
                        <Typography variant="caption" color="text.secondary">
                          {mailchimpConnection?.selectedAudienceName || 'No audience selected'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button size="small" variant="outlined" onClick={handleOpenMailchimpAudienceDialog}>
                          Audience
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setMailchimpDisconnectConfirmOpen(true)}
                          disabled={!canManageMail}
                        >
                          Disconnect
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={!canManageMail || connectingMailchimp}
                      onClick={() => setPrivacyConsentIntegration('mailchimp')}
                    >
                      {connectingMailchimp ? 'Redirecting…' : 'Connect Mailchimp'}
                    </Button>
                  )
                }
              >
                {mailchimpError ? (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {mailchimpError}
                  </Typography>
                ) : null}
                {mailchimpSuccess ? (
                  <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                    {mailchimpSuccess}
                  </Typography>
                ) : null}
                {!canManageMail && !mailchimpConnected ? (
                  <Typography variant="caption" color="text.secondary">
                    Only owners or admins can connect Mailchimp.
                  </Typography>
                ) : null}
              </IntegrationCard>
            ) : (
              <IntegrationCard
                icon={<BrandIcon src={MAILCHIMP_SVG} alt="" size={28} />}
                title="Mailchimp"
                description="Sync couples and opted-in enquiries to your audience. Available on plans with Mailchimp integration."
              >
                <UpgradeCTA reason="Mailchimp integration" compact />
              </IntegrationCard>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {canUseCalendar ? (
              <IntegrationCard
                icon={<Calendar size={28} weight="regular" />}
                title="Calendar sync"
                description="Google Calendar OAuth plus an iCal link for Outlook, Apple Calendar, and other apps."
                footer={
                  <Button variant="contained" onClick={() => setCalendarDialogOpen(true)}>
                    Manage calendar sync
                  </Button>
                }
              />
            ) : (
              <IntegrationCard
                icon={<Calendar size={28} weight="regular" />}
                title="Calendar sync"
                description="See bookings alongside your own calendar. Unlock Google Calendar and optional iCal feeds."
              >
                <UpgradeCTA reason="calendar sync" compact />
              </IntegrationCard>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <IntegrationCard
              icon={<Sparkle size={28} weight="regular" />}
              title="More soon"
              description="We are building more plug-ins and connections. Check back here as we release them."
              footer={
                <Button variant="outlined" disabled startIcon={<PlugsConnected size={18} />}>
                  Coming soon
                </Button>
              }
            />
          </Grid>
        </Grid>

        <MailConnectionSettings
          open={mailDialogOpen}
          onClose={() => {
            setMailDialogOpen(false);
            loadMailStatus();
          }}
          businessId={businessId}
          canManage={canManageMail}
        />

        {businessId ? (
          <CalendarSyncSettings
            open={calendarDialogOpen}
            onClose={() => setCalendarDialogOpen(false)}
            businessId={businessId}
          />
        ) : null}

        <Dialog
          open={mailchimpAudienceOpen}
          onClose={() => !savingMailchimpAudience && setMailchimpAudienceOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Choose Mailchimp audience</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Select the audience where Idotoo should add couples and opted-in enquiries.
            </DialogContentText>
            <FormControl fullWidth>
              <InputLabel id="mailchimp-audience-label">Audience</InputLabel>
              <Select
                labelId="mailchimp-audience-label"
                value={selectedAudienceId}
                label="Audience"
                onChange={(event) => setSelectedAudienceId(event.target.value)}
                disabled={mailchimpAudiencesLoading || savingMailchimpAudience}
              >
                {mailchimpAudiences.map((audience) => (
                  <MenuItem key={audience.id} value={audience.id}>
                    {audience.name} ({audience.memberCount})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {mailchimpAudiencesLoading ? (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMailchimpAudienceOpen(false)} disabled={savingMailchimpAudience}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveMailchimpAudience} disabled={savingMailchimpAudience}>
              {savingMailchimpAudience ? 'Saving…' : 'Save audience'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={mailchimpDisconnectConfirmOpen}
          onClose={() => !disconnectingMailchimp && setMailchimpDisconnectConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Disconnect Mailchimp?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Idotoo will stop sending new couples and enquiries to Mailchimp until you connect it again.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMailchimpDisconnectConfirmOpen(false)} disabled={disconnectingMailchimp}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleMailchimpDisconnect} disabled={disconnectingMailchimp}>
              {disconnectingMailchimp ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={disconnectConfirmOpen}
          onClose={() => !disconnecting && setDisconnectConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Disconnect inbox?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Idotoo will stop syncing this mailbox and remove stored access tokens. Your team will not see or send
              email from this inbox in Idotoo until you connect it again.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisconnectConfirmOpen(false)} disabled={disconnecting}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleMailDisconnect} disabled={disconnecting}>
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </DialogActions>
        </Dialog>

        <IntegrationPrivacyConsentDialog
          open={Boolean(privacyConsentIntegration)}
          integration={privacyConsentIntegration}
          onClose={() => setPrivacyConsentIntegration(null)}
          onConfirm={handlePrivacyConsentConfirm}
          confirming={
            connectingMailchimp ||
            connectingMail === privacyConsentIntegration
          }
        />
    </Container>
  );
}

export default SupplierAppsPage;
